import DiscoverSvg from "../../../../../../assets/icons/partial/discover.svg?react";
import CreateSvg from "../../../../../../assets/icons/partial/create.svg?react";
import ShareSvg from "../../../../../../assets/icons/partial/share.svg?react";
import { ShowChapterModulesButton } from "./ShowChapterModulesButton.jsx";
import { NavigationGroupTitle } from "./NavigationGroupTitle.jsx";
import { styles } from "./NavigationGroup.css.ts";

/**
 * Variable to map type values to corresponding labels and icons.
 *
 * @type {Object}
 * @property {Object} discover - Object representing the "discover" type.
 * @property {string} discover.label - The label for the "discover" type.
 * @property {React.Component} discover.icon - The icon component for the "discover" type.
 * @property {Object} create - Object representing the "create" type.
 * @property {string} create.label - The label for the "create" type.
 * @property {React.Component} create.icon - The icon component for the "create" type.
 * @property {Object} share - Object representing the "share" type.
 * @property {string} share.label - The label for the "share" type.
 * @property {React.Component} share.icon - The icon component for the "share" type.
 */
const typeToIcon = {
  discover: DiscoverSvg,
  create: CreateSvg,
  share: ShareSvg,
};

/**
 * The CHAPTER_STATES object represents different states.
 *
 * @constant
 * @type {Object}
 * @property {string} ACTIVE - Represents the active state.
 * @property {string} INACTIVE - Represents the inactive state.
 */
const CHAPTER_STATES = {
  ACTIVE: "active",
  INACTIVE: "inactive",
  DISABLED: "disabled",
};

/**
 * Represents the possible states for a group.
 *
 * @namespace
 * @property {string} ACTIVE - The active state of the group.
 * @property {string} DEFAULT - The default state of the group.
 */
export const GROUP_STATES = {
  ACTIVE: "active",
  DEFAULT: "default",
  DISABLED: "disabled",
};

/**
 * Determines the state of a phase by checking whether it is disabled, or whether we are currently in this phase.
 *
 * @param {{ type: string, name: string, disabled: boolean }} currentPhase - The current phase object.
 * @param {{ type: string, name: string, disabled: boolean }} phaseToCompare - The phase object to compare against.
 * @param {boolean} disableSome
 * @returns {string} - The state of the phase (`DISABLED`, `ACTIVE`, or `DEFAULT`).
 */
const getPhaseState = (currentPhase, phaseToCompare, disableSome = false) => {
  if (disableSome && phaseToCompare.disabled) return GROUP_STATES.DISABLED;
  if (
    currentPhase.type === phaseToCompare.type &&
    currentPhase.name === phaseToCompare.name
  ) {
    return GROUP_STATES.ACTIVE;
  }
  return GROUP_STATES.DEFAULT;
};

/**
 * Returns the state of a stage based on the currently open stage's slug and own stage slug.
 *
 * @param {{ slug: string }} currentStage - The currently opened stage.
 * @param {{ slug: string, disabled: boolean }} stageToCompare - The stage to compare with
 * @param {boolean} disableSome
 * @param {string} phaseState
 * @returns {string}
 */
const getStageState = (
  currentStage,
  stageToCompare,
  disableSome = false,
  phaseState,
) => {
  if (
    disableSome &&
    (phaseState === CHAPTER_STATES.DISABLED || stageToCompare.disabled)
  )
    return CHAPTER_STATES.DISABLED;
  if (currentStage?.slug === stageToCompare.slug) {
    return CHAPTER_STATES.ACTIVE;
  }
  return CHAPTER_STATES.INACTIVE;
};

/**
 * Displays the "discover", "create", "share" or other heading on the high-level navigation,
 * plus the buttons for opening the navigation for this part of this spark part (e.g. different chapters)
 * on the spark overview page. *
 * @param {Object} props - The properties for the NavigationGroup component.
 * @param {Object} props.phase - The phase data for the navigation group.
 * @param {Object} props.currentPhase - The currently active phase object.
 * @param {Object} props.currentStage - The currently active stage object.
 * @param {Function} props.handleStageClick - Function to set the currently active position.
 * @param {boolean} [props.disableSome=false] - Flag indicating if interaction with some elements should be disabled.
 *
 * @returns {JSX.Element} A navigation group component.
 */
export const NavigationGroup = ({
  phase,
  currentPhase,
  currentStage,
  handleStageClick,
  disableSome = false,
}) => {
  const Icon = typeToIcon[phase.type] || null;
  const phaseState = getPhaseState(currentPhase, phase, disableSome);
  return (
    <div className={styles.navigationGroup}>
      <NavigationGroupTitle
        groupState={phaseState}
        label={phase.name}
        Icon={Icon}
      />
      <div
        className={styles.navigationGroupChapterButtonList}
        id="chapter-navigation"
      >
        {phase.stages?.map((stage) => (
          <ShowChapterModulesButton
            key={stage.slug}
            state={getStageState(currentStage, stage, disableSome, phaseState)}
            onClick={() => {
              if (!(disableSome && (phase.disabled || stage.disabled))) {
                handleStageClick(phase, stage);
              }
            }}
          >
            {stage.name}
          </ShowChapterModulesButton>
        ))}
      </div>
    </div>
  );
};
